import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { formatDate } from '@/utils/dateUtils';

// Expiry reminders of the Beitragsfaktor and the SBU confirmation (#2004) and
// of the yearly KmbB review meeting (kmbb, #2007): workflow items of category
// subsidy_expiry, raised by the daily sweep 90, 60 and 30 days before the
// valid-until date and once it has passed.

export type SubsidyExpiryDocument = 'bf' | 'sbu' | 'kmbb';

export const SUBSIDY_EXPIRY_DOCUMENT_LABELS: Record<SubsidyExpiryDocument, MessageDescriptor> = {
  bf: msg({ id: 'subsidyExpiry.document.bf', message: 'Beitragsfaktor' }),
  sbu: msg({ id: 'subsidyExpiry.document.sbu', message: 'SBU' }),
  kmbb: msg({ id: 'subsidyExpiry.document.kmbb', message: 'KmbB review meeting' }),
};

type Translate = { _: (d: MessageDescriptor) => string };
type TranslateWithValues = { _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string };

export function subsidyExpiryDocumentLabel(i18n: Translate, document?: string): string {
  const descriptor = document ? SUBSIDY_EXPIRY_DOCUMENT_LABELS[document as SubsidyExpiryDocument] : undefined;
  return descriptor ? i18n._(descriptor) : (document ?? '');
}

/** The inbox line of a reminder: "<document> valid until <date>: <child>". */
export function subsidyExpirySummary(
  i18n: Translate & TranslateWithValues,
  payload: Record<string, unknown> | undefined,
): string {
  const document = subsidyExpiryDocumentLabel(i18n, typeof payload?.document === 'string' ? payload.document : undefined);
  const child = typeof payload?.child_name === 'string' ? payload.child_name : '';
  const until = typeof payload?.valid_until === 'string' ? formatDate(payload.valid_until) : '';
  const expired = typeof payload?.threshold === 'number' && payload.threshold === 0;
  const line = expired
    ? i18n._({ id: 'subsidyExpiry.summary.expired', message: '{document} expired on {date}', values: { document, date: until } })
    : i18n._({ id: 'subsidyExpiry.summary.expires', message: '{document} valid until {date}', values: { document, date: until } });
  return child ? `${line}: ${child}` : line;
}
